import React, { useState } from 'react';
import { X, Save, ClipboardCheck, Truck } from 'lucide-react';
import { useRoofTypes } from '@/hooks/useRoofTypes';

/**
 * Streamlined drive-by / roadside appraisal quick-add.
 * Mirrors the Access "DriveByAppraisalFrm1" workflow — captures the bare minimum
 * needed to log a roadside roof inspection in 30 seconds.
 */
export default function DriveByModal({ api, onClose, onCreated }) {
  const roofTypes = useRoofTypes(api);
  const [form, setForm] = useState({
    address_num: '',
    address_street: '',
    suburb: '',
    city: '',
    area_code: '',
    name_first: '',
    name_last: '',
    phone_mobile: '',
    email: '',
    roof_type: '',
    roof_colour: '',
    roof_state: '',
    roof_age: '',
    roof_stories: 1,
    appraisal_by: '',
    comments: '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!form.address_street.trim()) {
      setError('Street name is required');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const { data } = await api.post('/appraisals/quick-drive-by', form);
      onCreated?.(data);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to save');
    }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-md max-w-2xl w-full max-h-[90vh] overflow-y-auto thin-scroll" onClick={e => e.stopPropagation()} data-testid="drive-by-modal">
        <div className="sticky top-0 z-10 bg-white border-b border-[#E2E2DF] px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-md bg-purple-50 flex items-center justify-center ring-1 ring-purple-100">
              <Truck size={18} className="text-purple-600" strokeWidth={2} />
            </div>
            <div>
              <h2 className="text-lg font-extrabold text-slate-900 font-[Manrope] tracking-tight">Drive-by Appraisal</h2>
              <p className="text-xs text-slate-500 mt-0.5">Quick roadside roof inspection — only the essentials</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded text-slate-400 hover:text-slate-700 transition-colors"><X size={18} /></button>
        </div>

        <form onSubmit={submit} className="p-6 space-y-5">
          {/* Property */}
          <section>
            <h3 className="eyebrow mb-3">Property</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">Street #</label>
                <input data-testid="db-address-num" value={form.address_num} onChange={e => set('address_num', e.target.value)} placeholder="42" className="w-full px-3 py-2 border border-[#E2E2DF] rounded-md text-sm" />
              </div>
              <div className="md:col-span-2">
                <label className="block text-xs font-semibold text-slate-600 mb-1">Street name <span className="text-rose-500">*</span></label>
                <input data-testid="db-address-street" value={form.address_street} onChange={e => set('address_street', e.target.value)} placeholder="Aotearoa Road" required className="w-full px-3 py-2 border border-[#E2E2DF] rounded-md text-sm" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">Suburb</label>
                <input data-testid="db-suburb" value={form.suburb} onChange={e => set('suburb', e.target.value)} className="w-full px-3 py-2 border border-[#E2E2DF] rounded-md text-sm" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">City</label>
                <input data-testid="db-city" value={form.city} onChange={e => set('city', e.target.value)} className="w-full px-3 py-2 border border-[#E2E2DF] rounded-md text-sm" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">Area code</label>
                <input data-testid="db-area" value={form.area_code} onChange={e => set('area_code', e.target.value)} placeholder="RD 7" className="w-full px-3 py-2 border border-[#E2E2DF] rounded-md text-sm" />
              </div>
            </div>
          </section>

          {/* Owner contact (optional) */}
          <section>
            <h3 className="eyebrow mb-3">Owner contact <span className="text-slate-400 text-[10px] normal-case ml-1">(optional — leave blank if you didn't speak to anyone)</span></h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <input data-testid="db-name-first" value={form.name_first} onChange={e => set('name_first', e.target.value)} placeholder="First name" className="px-3 py-2 border border-[#E2E2DF] rounded-md text-sm" />
              <input data-testid="db-name-last" value={form.name_last} onChange={e => set('name_last', e.target.value)} placeholder="Last name" className="px-3 py-2 border border-[#E2E2DF] rounded-md text-sm" />
              <input data-testid="db-phone" value={form.phone_mobile} onChange={e => set('phone_mobile', e.target.value)} placeholder="Mobile" className="px-3 py-2 border border-[#E2E2DF] rounded-md text-sm" />
              <input data-testid="db-email" type="email" value={form.email} onChange={e => set('email', e.target.value)} placeholder="Email" className="px-3 py-2 border border-[#E2E2DF] rounded-md text-sm" />
            </div>
          </section>

          {/* Roof snapshot */}
          <section>
            <h3 className="eyebrow mb-3">Roof snapshot</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">Type</label>
                <input data-testid="db-roof-type" list="rt-list" value={form.roof_type} onChange={e => set('roof_type', e.target.value)} placeholder="Iron" className="w-full px-3 py-2 border border-[#E2E2DF] rounded-md text-sm" />
                <datalist id="rt-list">{[...roofTypes, 'Other / Unsure'].map(rt => <option key={rt}>{rt}</option>)}</datalist>
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">Colour</label>
                <input data-testid="db-roof-colour" value={form.roof_colour} onChange={e => set('roof_colour', e.target.value)} placeholder="Karaka green" className="w-full px-3 py-2 border border-[#E2E2DF] rounded-md text-sm" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">Age (yrs)</label>
                <input data-testid="db-roof-age" value={form.roof_age} onChange={e => set('roof_age', e.target.value)} placeholder="20+" className="w-full px-3 py-2 border border-[#E2E2DF] rounded-md text-sm" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">Stories</label>
                <select data-testid="db-stories" value={form.roof_stories} onChange={e => set('roof_stories', Number(e.target.value))} className="w-full px-3 py-2 border border-[#E2E2DF] rounded-md text-sm bg-white">
                  <option value={1}>1 storey</option>
                  <option value={2}>2 storey</option>
                  <option value={3}>3 storey</option>
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="block text-xs font-semibold text-slate-600 mb-1">Condition</label>
                <select data-testid="db-roof-state" value={form.roof_state} onChange={e => set('roof_state', e.target.value)} className="w-full px-3 py-2 border border-[#E2E2DF] rounded-md text-sm bg-white">
                  <option value="">— Select —</option>
                  <option>Excellent</option>
                  <option>Good</option>
                  <option>Fair</option>
                  <option>Poor</option>
                  <option>Failed</option>
                </select>
              </div>
            </div>
          </section>

          {/* Notes */}
          <section>
            <h3 className="eyebrow mb-3">Notes & next steps</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
              <input data-testid="db-appraiser" value={form.appraisal_by} onChange={e => set('appraisal_by', e.target.value)} placeholder="Appraised by" className="px-3 py-2 border border-[#E2E2DF] rounded-md text-sm" />
            </div>
            <textarea data-testid="db-comments" value={form.comments} onChange={e => set('comments', e.target.value)} rows={3} placeholder="What you saw — moss/lichen, lifted ridges, rusted spouting, etc." className="w-full px-3 py-2 border border-[#E2E2DF] rounded-md text-sm" />
          </section>

          {error && <div className="px-4 py-2.5 bg-rose-50 border border-rose-200 text-rose-700 rounded-md text-sm" data-testid="db-error">{error}</div>}

          <div className="flex items-center justify-end gap-2 pt-2 border-t border-[#E2E2DF]">
            <button type="button" onClick={onClose} className="px-4 py-2 border border-[#E2E2DF] rounded-md text-sm font-semibold text-slate-600 hover:bg-slate-50 hover:border-slate-300 transition-colors">Cancel</button>
            <button data-testid="db-save-btn" type="submit" disabled={saving} className="flex items-center gap-1.5 px-5 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-md text-sm font-semibold disabled:opacity-50 shadow-sm hover:shadow-md hover:shadow-purple-500/20 transition-all">
              <Save size={14} strokeWidth={2.5} /> {saving ? 'Saving...' : 'Save & Open'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
